var _react_refresh_temp_1;
_react_refresh_temp_1 = $RefreshSig$();
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { translate, useToastService } from '@lucidlink/uicore';
import { usePlugFilespaceClient } from '#query-hooks';
import { GuestLinkKeyFactory } from '../query-key-factories/GuestLinkKeyFactory';
export const useAddEmailsToGuestLink = ({ filespaceId, workspaceId, }) => {
    _react_refresh_temp_1();
    const { plugFilespaceClient } = usePlugFilespaceClient();
    const { addToast } = useToastService();
    const queryClient = useQueryClient();
    const mutation = useMutation({
        mutationKey: GuestLinkKeyFactory.mutations({
            filespaceId,
            workspaceId,
        }),
        mutationFn: async (params) => plugFilespaceClient({
            action: (fsClient) => fsClient
                .guestLinksManagement()
                .addEmailsToGuestLink(params),
            filespaceId,
            workspaceId,
        }),
        onMutate: async (params) => {
            const exactKey = GuestLinkKeyFactory.exact({
                workspaceId,
                filespaceId,
                linkId: params.guestLinkId,
            });
            await queryClient.cancelQueries({ queryKey: exactKey });
            const prevData = queryClient.getQueryData(exactKey);
            const existingEmails = new Set(prevData?.guestLinkAcl.map((acl) => acl.email));
            const addedEmails = params.emails.filter((email) => !existingEmails.has(email));
            if (prevData) {
                queryClient.setQueryData(exactKey, {
                    ...prevData,
                    guestLinkAcl: [
                        ...prevData.guestLinkAcl,
                        ...addedEmails.map((email) => ({
                            guestLinkId: params.guestLinkId,
                            email,
                            createdByMemberId: '',
                            createdAt: new Date(),
                            lastUsedAt: null,
                            isOptimistic: true,
                        })),
                    ],
                });
            }
            return { addedEmails };
        },
        onError: (_, params, onMutationResult) => {
            const addedEmails = onMutationResult?.addedEmails ?? [];
            // Undo only this mutation's own optimistic entries so a
            // concurrent mutation's optimistic state is left untouched.
            queryClient.setQueryData(GuestLinkKeyFactory.exact({
                workspaceId,
                filespaceId,
                linkId: params.guestLinkId,
            }), (old) => old && {
                ...old,
                guestLinkAcl: old.guestLinkAcl.filter((acl) => !(acl.isOptimistic &&
                    addedEmails.includes(acl.email))),
            });
            addToast({
                'data-testid': 'add-emails-to-guest-link-error',
                kind: 'negative',
                content: {
                    message: translate('Failed to add guests to the link'),
                },
            });
        },
        onSuccess: () => {
            addToast({
                'data-testid': 'add-emails-to-guest-link-success',
                kind: 'positive',
                content: {
                    message: translate('Guests added successfully'),
                },
            });
        },
        onSettled: (_data, _error, params) => {
            const pendingMutations = queryClient.isMutating({
                mutationKey: GuestLinkKeyFactory.mutations({
                    filespaceId,
                    workspaceId,
                }),
            });
            const listRefetch = queryClient.refetchQueries({
                queryKey: GuestLinkKeyFactory.list({
                    filespaceId,
                    workspaceId,
                }),
            });
            // Refetching `exact` while a later guest-link mutation is pending
            // would wipe its optimistic ACL state; the last one refetches it.
            if (pendingMutations > 1) {
                return listRefetch;
            }
            return Promise.all([
                listRefetch,
                queryClient.refetchQueries({
                    queryKey: GuestLinkKeyFactory.exact({
                        workspaceId,
                        filespaceId,
                        linkId: params.guestLinkId,
                    }),
                }),
            ]);
        },
    });
    return mutation;
};
_react_refresh_temp_1(useAddEmailsToGuestLink, "KrM3mhTkTlZAjWS6DCmlJKC0EYA=", false, () => [usePlugFilespaceClient, useToastService, useQueryClient, useMutation]);
